import {
  Flow,
  UnicoCreateProcessResponse,
  UnicoProcessService,
  UnicoProcessDocument
} from "@repo/core/services/entities/Unico";
import type { NameGender } from "@repo/core/services/entities/WayupRequisitions";
import { CreateProposalInputDTO } from "@repo/core/services/entities/Proposals";
import {
  isSessionInvalidated,
  SessionExpiredFlowError
} from "@repo/core/services/session/sessionEpoch";
import { PreProposalData } from "../../store/preProposalStore";
import {
  limitParentName,
  sanitizeParentNameForApi
} from "@repo/core/utils/parentName.utils";
import {
  CNHDocumentData,
  RGDocumentData,
  CINDocumentData,
  PassportDocumentData,
  UnicoDocument
} from "@repo/core/screens/PreProposal/screens/BiometryValidation/types";

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Converts ISO date string to DD/MM/YYYY format
 * Parses the ISO string directly to avoid timezone conversion issues
 * (e.g. "1996-08-06T00:00:00Z" in UTC-3 would incorrectly become Aug 5 with Date)
 */
const isValidDateParts = (
  day: number,
  month: number,
  year: number
): boolean => {
  if (
    !Number.isInteger(day) ||
    !Number.isInteger(month) ||
    !Number.isInteger(year)
  ) {
    return false;
  }
  if (year < 1900 || year > new Date().getFullYear()) {
    return false;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const formatDatePartsToDDMMYYYY = (
  day: number,
  month: number,
  year: number
): string | undefined => {
  if (!isValidDateParts(day, month, year)) {
    return undefined;
  }

  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
};

const formatDateToDDMMYYYY = (dateString?: string): string | undefined => {
  const normalizedDate = dateString?.trim();
  if (!normalizedDate) return undefined;
  try {
    const isoDateMatch = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDateMatch) {
      return formatDatePartsToDDMMYYYY(
        parseInt(isoDateMatch[3], 10),
        parseInt(isoDateMatch[2], 10),
        parseInt(isoDateMatch[1], 10)
      );
    }

    const brDateMatch = normalizedDate.match(
      /^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/
    );
    if (brDateMatch) {
      return formatDatePartsToDDMMYYYY(
        parseInt(brDateMatch[1], 10),
        parseInt(brDateMatch[2], 10),
        parseInt(brDateMatch[3], 10)
      );
    }

    const compactDateMatch = normalizedDate.match(/^(\d{2})(\d{2})(\d{4})$/);
    if (compactDateMatch) {
      return formatDatePartsToDDMMYYYY(
        parseInt(compactDateMatch[1], 10),
        parseInt(compactDateMatch[2], 10),
        parseInt(compactDateMatch[3], 10)
      );
    }

    return undefined;
  } catch {
    return undefined;
  }
};

const normalizeComparableText = (value?: string): string =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const normalizePersonName = (value?: string): string | undefined => {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized || undefined;
};

const hasNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const hasValidErpDateString = (value: unknown): value is string =>
  hasNonEmptyString(value) && !/^0001-01-01/.test(value.trim());

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const preserveNonEmptyStringFieldsFromStore = (
  merged: PreProposalData,
  storeSnapshot: Partial<PreProposalData>,
  fields: Array<keyof PreProposalData>
) => {
  const target = merged as Record<string, unknown>;

  for (const field of fields) {
    const storeValue = storeSnapshot[field];
    const mergedValue = merged[field];

    if (hasNonEmptyString(storeValue) && !hasNonEmptyString(mergedValue)) {
      target[String(field)] = storeValue.trim();
    }
  }
};

const getFirstString = (
  data: Record<string, unknown>,
  keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = data[key];
    if (hasNonEmptyString(value)) {
      return value.trim();
    }
  }

  return undefined;
};

const getBirthDate = (data: Record<string, unknown>): string | undefined =>
  formatDateToDDMMYYYY(
    getFirstString(data, [
      "dataNascimento",
      "data_nascimento",
      "dtNascimento",
      "dt_nascimento",
      "dataNasc",
      "data_nasc",
      "nascimento",
      "birthDate",
      "birth_date",
      "dateOfBirth",
      "date_of_birth"
    ])
  );

const getIssueDate = (data: Record<string, unknown>): string | undefined =>
  formatDateToDDMMYYYY(
    getFirstString(data, [
      "dataEmissao",
      "data_emissao",
      "dtEmissao",
      "dt_emissao",
      "issueDate",
      "issue_date"
    ])
  );

const getStringArray = (
  data: Record<string, unknown>,
  keys: string[]
): string[] => {
  for (const key of keys) {
    const value = data[key];
    if (Array.isArray(value)) {
      return value.filter(hasNonEmptyString).map((item) => item.trim());
    }
  }

  return [];
};

const isSuspiciousPersonName = (value?: string): boolean => {
  const normalized = normalizeComparableText(value);
  if (!normalized) {
    return true;
  }
  if (normalized.length < 5 || normalized.length > 120) {
    return true;
  }
  if (!/[A-Z]/.test(normalized) || /\d/.test(normalized)) {
    return true;
  }
  if (/\b\d{1,2}\s+\d{1,2}\s+\d{2,4}\b/.test(normalized)) {
    return true;
  }

  return [
    "CARTORIO",
    "REGISTRO CIVIL",
    "CERTIDAO",
    "CERTIDAO DE NASCIMENTO",
    "NATURALIDADE",
    "COMARCA",
    "LIVRO",
    "FOLHA",
    "TERMO",
    "MATRICULA",
    "OFICIO",
    "TABELIAO"
  ].some((keyword) => normalized.includes(keyword));
};

const getUniqueValidNames = (names: string[]): string[] => {
  const uniqueNames: string[] = [];
  const seen = new Set<string>();

  for (const name of names) {
    const normalizedName = normalizePersonName(name);
    if (!normalizedName || isSuspiciousPersonName(normalizedName)) {
      continue;
    }
    const comparableName = normalizeComparableText(normalizedName);
    if (seen.has(comparableName)) {
      continue;
    }
    seen.add(comparableName);
    uniqueNames.push(normalizedName);
  }

  return uniqueNames;
};

type OcrMappingOptions = {
  currentProposalData?: Partial<PreProposalData> | null;
};

const getFiliacaoNames = (data: Record<string, unknown>): string[] =>
  getUniqueValidNames(
    getStringArray(data, ["filiacao", "filiação", "parents", "parentes"])
  );

const getFiliacaoCandidates = (data: Record<string, unknown>): string[] => {
  const candidates: string[] = [];
  const names = getStringArray(data, [
    "filiacao",
    "filiação",
    "parents",
    "parentes"
  ]);

  for (const name of names) {
    const normalizedName = normalizePersonName(name);
    if (!normalizedName || isSuspiciousPersonName(normalizedName)) {
      continue;
    }
    candidates.push(normalizedName);
    if (candidates.length === 2) {
      break;
    }
  }

  return candidates;
};

const getExplicitMotherName = (
  data: Record<string, unknown>
): string | undefined => {
  const explicitMotherName = normalizePersonName(
    getFirstString(data, [
      "nomeMae",
      "nome_mae",
      "nomeDaMae",
      "nome_da_mae",
      "mae",
      "maeNome",
      "filiacaoMae",
      "filiacao_mae",
      "motherName",
      "mother_name",
      "genitora",
      "nomeGenitora",
      "nome_genitora"
    ])
  );

  if (explicitMotherName && !isSuspiciousPersonName(explicitMotherName)) {
    return limitParentName(explicitMotherName);
  }

  return undefined;
};

const resolveMotherName = (
  data: Record<string, unknown>,
  _options?: OcrMappingOptions
): string | undefined => {
  const explicitMotherName = getExplicitMotherName(data);
  if (explicitMotherName) {
    return explicitMotherName;
  }

  const filiacao = getFiliacaoNames(data);

  return filiacao[1] ? limitParentName(filiacao[1]) : undefined;
};

const getExplicitFatherName = (
  data: Record<string, unknown>
): string | undefined => {
  const explicitFatherName = normalizePersonName(
    getFirstString(data, [
      "nomePai",
      "nome_pai",
      "nomeDoPai",
      "nome_do_pai",
      "pai",
      "paiNome",
      "filiacaoPai",
      "filiacao_pai",
      "fatherName",
      "father_name",
      "genitor",
      "nomeGenitor",
      "nome_genitor"
    ])
  );

  if (explicitFatherName && !isSuspiciousPersonName(explicitFatherName)) {
    return sanitizeParentNameForApi(explicitFatherName);
  }

  return undefined;
};

const resolveFatherName = (
  data: Record<string, unknown>
): string | undefined => {
  const explicitFatherName = getExplicitFatherName(data);
  if (explicitFatherName) {
    return explicitFatherName;
  }

  const filiacao = getFiliacaoNames(data);

  return sanitizeParentNameForApi(filiacao[0]);
};

/**
 * Removes "UF_" prefix from state code if present (e.g., "UF_SP" -> "SP")
 */
const parseUfEmissor = (ufEmissor?: string): string | undefined => {
  if (!ufEmissor) return undefined;
  return ufEmissor.replace(/^UF_/, "");
};

/**
 * Extracts city and state from location string (e.g., "Curitiba PR" -> { cidade: "Curitiba", uf: "PR" })
 */
const parseLocalEmissao = (
  localEmissao?: string
): { cidade: string | undefined; uf: string | undefined } => {
  if (!localEmissao) return { cidade: undefined, uf: undefined };

  const parts = localEmissao.trim().split(/\s+/);
  const uf = parts[parts.length - 1] || undefined;
  const cidade = parts.slice(0, -1).join(" ") || undefined;

  return { cidade, uf };
};

/**
 * Keeps only the first 15 digits for NRODOCIDENT, preserving leading zeros.
 */
const sanitizeDocumentNumber = (value?: string): string | undefined => {
  return value?.replace(/\D/g, "").slice(0, 15) || undefined;
};

const removeUndefinedFields = <T extends Record<string, unknown>>(
  obj: T
): Partial<T> => {
  const cleaned = { ...obj };
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

export enum DocumentType {
  CNH = "CNH",
  RG = "RG",
  CIN = "CIN",
  PASSAPORTE = "PASSAPORTE"
}

/**
 * Identifies document type based on available fields
 * Uses unique field combinations to distinguish between document types
 */
const identifyDocumentType = (
  data: Record<string, unknown>
): DocumentType | null => {
  if (data.paisEmissor || data.autoridade || data.sobrenome) {
    return DocumentType.PASSAPORTE;
  }

  if (data.dataHabilitacao || data.categoria || data.renachNumero) {
    return DocumentType.CNH;
  }

  // CIN and RG both have orgaoEmissor and ufEmissor
  // RG typically has additional fields like cpfNumero, ctpsNumero
  if (data.orgaoEmissor && data.ufEmissor) {
    if (!data.cpfNumero && !data.ctpsNumero) {
      return DocumentType.CIN;
    }
    return DocumentType.RG;
  }

  if (data.orgaoEmissor || data.ufEmissor) {
    return DocumentType.RG;
  }

  return null;
};

// ============================================================================
// Private mapping functions
// ============================================================================

const mapCNHDocumentToPreProposalData = (
  document: UnicoDocument,
  options?: OcrMappingOptions
): Partial<PreProposalData> => {
  const data = document?.doc?.data as CNHDocumentData["content"];

  if (!data) {
    return {};
  }

  const rawData = data as Record<string, unknown>;
  const { uf } = parseLocalEmissao(data.localEmissao);
  const rgNumero = sanitizeDocumentNumber(data.rgNumero);

  const mappedData: Partial<PreProposalData> = {
    datNascimento: getBirthDate(rawData),
    strNomeMae: resolveMotherName(rawData, options),
    strNomePai: resolveFatherName(rawData),
    strNroDocIdent: rgNumero || sanitizeDocumentNumber(data.numero),
    strUfEmiDocIdent: uf,
    strDataEmiDocIdent: getIssueDate(rawData),
    strTipoDocIdent: DocumentType.CNH
  };

  return removeUndefinedFields(mappedData);
};

const mapRGDocumentToPreProposalData = (
  document: UnicoDocument,
  options?: OcrMappingOptions
): Partial<PreProposalData> => {
  const data = document?.doc?.data as RGDocumentData["content"];

  if (!data) {
    return {};
  }

  const rawData = data as Record<string, unknown>;
  const ufEmissor = parseUfEmissor(data.ufEmissor);

  const mappedData: Partial<PreProposalData> = {
    datNascimento: getBirthDate(rawData),
    strNomeMae: resolveMotherName(rawData, options),
    strNomePai: resolveFatherName(rawData),
    strNroDocIdent: sanitizeDocumentNumber(data.numero),
    strUfEmiDocIdent: ufEmissor,
    strDataEmiDocIdent: getIssueDate(rawData),
    strTipoDocIdent: DocumentType.RG
  };

  return removeUndefinedFields(mappedData);
};

const mapCINDocumentToPreProposalData = (
  document: UnicoDocument,
  options?: OcrMappingOptions
): Partial<PreProposalData> => {
  const data = document?.doc?.data as CINDocumentData["content"];

  if (!data) {
    return {};
  }

  const rawData = data as Record<string, unknown>;
  const ufEmissor = parseUfEmissor(data.ufEmissor);
  const { uf: ufLocalEmissao } = parseLocalEmissao(data.localEmissao);

  const ufFinal = ufEmissor || ufLocalEmissao;

  const mappedData: Partial<PreProposalData> = {
    datNascimento: getBirthDate(rawData),
    strNomeMae: resolveMotherName(rawData, options),
    strNomePai: resolveFatherName(rawData),
    strNroDocIdent: sanitizeDocumentNumber(data.numero),
    strUfEmiDocIdent: ufFinal,
    strDataEmiDocIdent: getIssueDate(rawData),
    strTipoDocIdent: DocumentType.CIN
  };

  return removeUndefinedFields(mappedData);
};

const mapPassportDocumentToPreProposalData = (
  document: UnicoDocument
): Partial<PreProposalData> => {
  const data = document?.doc?.data as PassportDocumentData["content"];

  if (!data) {
    return {};
  }

  const rawData = data as Record<string, unknown>;
  const mappedData: Partial<PreProposalData> = {
    datNascimento: getBirthDate(rawData),
    strNroDocIdent: sanitizeDocumentNumber(data.numero),
    strDataEmiDocIdent: getIssueDate(rawData),
    strTipoDocIdent: DocumentType.PASSAPORTE
  };

  return removeUndefinedFields(mappedData);
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Maps Unico document data to proposal format
 * @param document - Document returned by Unico
 * @param documentType - Document type (optional, will be auto-detected if not provided)
 * @returns Mapped object with PreProposalData fields
 */
export const mapDocumentToPreProposalData = (
  document: UnicoDocument,
  documentType?: DocumentType,
  options?: OcrMappingOptions
): Partial<PreProposalData> => {
  if (!document?.doc?.data) {
    return {};
  }

  const type =
    documentType ||
    identifyDocumentType(document.doc.data as Record<string, unknown>);

  if (!type) {
    console.error(
      "Document type could not be identified. Returning empty object."
    );
    return {};
  }

  switch (type) {
    case DocumentType.CNH:
      return mapCNHDocumentToPreProposalData(document, options);
    case DocumentType.RG:
      return mapRGDocumentToPreProposalData(document, options);
    case DocumentType.CIN:
      return mapCINDocumentToPreProposalData(document, options);
    case DocumentType.PASSAPORTE:
      return mapPassportDocumentToPreProposalData(document);
    default:
      console.error(
        `Unsupported document type: ${type}. Returning empty object.`
      );
      return {};
  }
};

/**
 * Maps Unico API doc.code to DocumentType enum
 */
const mapDocCodeToDocumentType = (code?: string): DocumentType | null => {
  if (!code) return null;
  const normalized = code.toUpperCase();
  if (normalized === "CNH") return DocumentType.CNH;
  if (normalized === "RG") return DocumentType.RG;
  if (normalized === "CIN") return DocumentType.CIN;
  if (normalized === "PASSAPORTE" || normalized === "PASSPORT")
    return DocumentType.PASSAPORTE;
  return null;
};

const countMappedFields = (mapped: Partial<PreProposalData>): number =>
  [
    mapped.datNascimento,
    mapped.strNomeMae,
    mapped.strNomePai,
    mapped.strNroDocIdent,
    mapped.strUfEmiDocIdent,
    mapped.strDataEmiDocIdent,
    mapped.strTipoDocIdent
  ].filter(hasNonEmptyString).length;

const scoreMappedDocument = (
  mapped: Partial<PreProposalData>,
  documentType: DocumentType | null,
  unicoDoc: UnicoProcessDocument
): number => {
  let score = countMappedFields(mapped);
  if (documentType) score += 5;
  if (hasNonEmptyString(mapped.datNascimento)) score += 4;
  if (hasNonEmptyString(mapped.strNroDocIdent)) score += 3;
  if (hasNonEmptyString(mapped.strNomeMae)) score += 2;
  if (unicoDoc.validate_doc === true) score += 2;
  if (unicoDoc.cpf_match === true) score += 1;
  if (unicoDoc.validate_doc === false) score -= 2;
  return score;
};

type BestOcrMapping = {
  mapped: Partial<PreProposalData>;
  rawData?: Record<string, unknown>;
};

const getBestOcrMapping = (
  processResponse: UnicoCreateProcessResponse,
  options?: OcrMappingOptions
): BestOcrMapping => {
  const services = processResponse?.process?.services;
  if (!services || !Array.isArray(services)) {
    return { mapped: {} };
  }

  let bestMapped: Partial<PreProposalData> = {};
  let bestRawData: Record<string, unknown> | undefined;
  let bestScore = 0;

  for (const service of services) {
    const unicoService = service as UnicoProcessService;
    const documents = unicoService?.documents;
    if (!documents || !Array.isArray(documents)) {
      continue;
    }

    for (const docItem of documents) {
      const unicoDoc = docItem as UnicoProcessDocument;
      if (!unicoDoc?.doc?.data) {
        continue;
      }

      const document: UnicoDocument = {
        doc: {
          data: unicoDoc.doc.data as UnicoDocument["doc"]["data"]
        }
      };
      const rawData = unicoDoc.doc.data as Record<string, unknown>;
      const documentType = mapDocCodeToDocumentType(unicoDoc.doc.code);
      const mapped = mapDocumentToPreProposalData(
        document,
        documentType || undefined,
        options
      );
      const score = scoreMappedDocument(mapped, documentType, unicoDoc);

      if (score > bestScore) {
        bestMapped = mapped;
        bestRawData = rawData;
        bestScore = score;
      }
    }
  }

  return { mapped: bestMapped, rawData: bestRawData };
};

/**
 * Extracts OCR data from Unico process response and maps to PreProposalData
 * Iterates process.services to find documents with OCR data (CNH, RG, CIN, Passport)
 * @param processResponse - Response from Unico getProcess API
 * @returns Partial PreProposalData with datNascimento, strNroDocIdent, strNomeMae, etc.
 */
export const extractOcrDataFromProcessResponse = (
  processResponse: UnicoCreateProcessResponse,
  options?: OcrMappingOptions
): Partial<PreProposalData> => getBestOcrMapping(processResponse, options).mapped;

type ValidatedFiliationOptions = OcrMappingOptions & {
  lookupNameGender: (name: string) => Promise<NameGender>;
};

const lookupConclusiveNameGender = async (
  name: string,
  lookupNameGender: ValidatedFiliationOptions["lookupNameGender"]
): Promise<"MASCULINO" | "FEMININO" | undefined> => {
  try {
    const gender = await lookupNameGender(name);
    return gender === "MASCULINO" || gender === "FEMININO"
      ? gender
      : undefined;
  } catch (error) {
    if (error instanceof SessionExpiredFlowError) {
      throw error;
    }
    if (isSessionInvalidated()) {
      throw new SessionExpiredFlowError();
    }
    return undefined;
  }
};

const resolveFiliationByGender = async (
  rawData: Record<string, unknown>,
  lookupNameGender: ValidatedFiliationOptions["lookupNameGender"]
): Promise<Pick<Partial<PreProposalData>, "strNomeMae" | "strNomePai">> => {
  const candidates = getFiliacaoCandidates(rawData);
  if (
    candidates.length === 2 &&
    normalizeComparableText(candidates[0]) ===
      normalizeComparableText(candidates[1])
  ) {
    return {};
  }

  const genders = await Promise.all(
    candidates.map((name) =>
      lookupConclusiveNameGender(name, lookupNameGender)
    )
  );
  if (
    genders.length === 2 &&
    genders[0] !== undefined &&
    genders[0] === genders[1]
  ) {
    return {};
  }

  const motherIndex = genders.findIndex((gender) => gender === "FEMININO");
  const fatherIndex = genders.findIndex((gender) => gender === "MASCULINO");
  const motherName =
    motherIndex >= 0 ? limitParentName(candidates[motherIndex]) : undefined;
  const fatherName =
    fatherIndex >= 0
      ? sanitizeParentNameForApi(candidates[fatherIndex])
      : undefined;

  return {
    ...(motherName ? { strNomeMae: motherName } : {}),
    ...(fatherName ? { strNomePai: fatherName } : {})
  };
};

export const extractOcrDataWithValidatedFiliation = async (
  processResponse: UnicoCreateProcessResponse,
  options: ValidatedFiliationOptions
): Promise<Partial<PreProposalData>> => {
  const { lookupNameGender, ...mappingOptions } = options;
  const { mapped, rawData } = getBestOcrMapping(
    processResponse,
    mappingOptions
  );
  const {
    strNomeMae: _positionalMotherName,
    strNomePai: _positionalFatherName,
    ...nonParentFields
  } = mapped;

  if (!rawData) {
    return nonParentFields;
  }

  const explicitMotherName = getExplicitMotherName(rawData);
  const explicitFatherName = getExplicitFatherName(rawData);
  if (explicitMotherName && explicitFatherName) {
    return {
      ...nonParentFields,
      strNomeMae: explicitMotherName,
      strNomePai: explicitFatherName
    };
  }

  const filiationNames = await resolveFiliationByGender(
    rawData,
    lookupNameGender
  );
  const derivedMotherName =
    filiationNames.strNomeMae &&
    normalizeComparableText(filiationNames.strNomeMae) !==
      normalizeComparableText(explicitFatherName)
      ? filiationNames.strNomeMae
      : undefined;
  const derivedFatherName =
    filiationNames.strNomePai &&
    normalizeComparableText(filiationNames.strNomePai) !==
      normalizeComparableText(explicitMotherName)
      ? filiationNames.strNomePai
      : undefined;

  return {
    ...nonParentFields,
    ...(explicitMotherName || derivedMotherName
      ? { strNomeMae: explicitMotherName || derivedMotherName }
      : {}),
    ...(explicitFatherName || derivedFatherName
      ? { strNomePai: explicitFatherName || derivedFatherName }
      : {})
  };
};

export const createMemoizedValidatedOcrDataLoader = (
  options: ValidatedFiliationOptions
) => {
  let resolution: Promise<Partial<PreProposalData>> | undefined;

  return (
    processResponse: UnicoCreateProcessResponse
  ): Promise<Partial<PreProposalData>> => {
    resolution ??= extractOcrDataWithValidatedFiliation(
      processResponse,
      options
    );
    return resolution;
  };
};

/**
 * Document number, parent's names and birth date from Unico OCR for POST Proposta/Alterar.
 */
export const pickBiometryIdentityFieldsForProposalAlter = (
  ocrData: Partial<PreProposalData>
): Partial<
  Pick<
    CreateProposalInputDTO,
    "strNroDocIdent" | "strNomeMae" | "strNomePai" | "datNascimento"
  >
> => {
  const out: Partial<
    Pick<
      CreateProposalInputDTO,
      "strNroDocIdent" | "strNomeMae" | "strNomePai" | "datNascimento"
    >
  > = {};
  if (hasNonEmptyString(ocrData.strNroDocIdent)) {
    out.strNroDocIdent = ocrData.strNroDocIdent.trim();
  }
  if (hasNonEmptyString(ocrData.strNomeMae)) {
    out.strNomeMae = limitParentName(ocrData.strNomeMae.trim());
  }
  const fatherName = sanitizeParentNameForApi(ocrData.strNomePai?.trim());
  if (fatherName) {
    out.strNomePai = fatherName;
  }
  if (hasNonEmptyString(ocrData.datNascimento)) {
    out.datNascimento = ocrData.datNascimento.trim();
  }
  return out;
};

export const mergeBiometryProposalAlterData = ({
  storeSnapshot,
  updatedProposalData,
  ocrData
}: {
  storeSnapshot: Partial<PreProposalData>;
  updatedProposalData: PreProposalData;
  ocrData: Partial<PreProposalData>;
}): PreProposalData => {
  const identityFromOcr = pickBiometryIdentityFieldsForProposalAlter(ocrData);
  const merged = {
    ...storeSnapshot,
    ...updatedProposalData,
    ...identityFromOcr
  } as PreProposalData;

  // Do not send issuing authority from OCR (or prior store) on Proposta/Alterar after biometry
  delete merged.strOrgaoEmissor;

  preserveNonEmptyStringFieldsFromStore(merged, storeSnapshot, [
    "strNome",
    "strCliente",
    "strNroDocIdent",
    "strTipoDocIdent",
    "strNomeMae",
    "strNomePai",
    "datNascimento",
    "strSexo",
    "strEstadoCivil",
    "strEmail",
    "strInfoEmail",
    "strProduto",
    "strBandeira",
    "strPontoAtendimento",
    "strAtendente",
    "strEndDdd1",
    "strEndFone1",
    "strEndTipoFone1",
    "strResTipoFone1",
    "strEndTipoResidencia",
    "strEndMesAno",
    "strResCep",
    "strResEndereco",
    "strResNumero",
    "strResBairro",
    "strResCidade",
    "strResUF",
    "strResComplemento",
    "strEndDdd2",
    "strEndFone2",
    "strEndTipoFone2",
    "strResDDD2",
    "strResFone2",
    "strResTipoFone2",
    "strAtivEmpresa",
    "strAtivCargo",
    "strAtivSetor",
    "strProfissao",
    "strAtivProfissao",
    "strClasseProfissional",
    "strRefPessNome1",
    "strRefPessRel1",
    "strRefPessFone1",
    "strRefPessDdd1",
    "strCorrespondencia",
    "strTipoPessoa",
    "strTipoProposta",
    "strOpcaoPagamentos",
    "strTipoCorrespondencia",
    "strEnvioFatura",
    "strReavaliacaoLimite"
  ]);

  const storeAdmissionDate = hasValidErpDateString(
    storeSnapshot.strTrabalhaDesde
  )
    ? storeSnapshot.strTrabalhaDesde.trim()
    : undefined;

  if (storeAdmissionDate && !hasValidErpDateString(merged.strTrabalhaDesde)) {
    merged.strTrabalhaDesde = storeAdmissionDate;
  }

  if (
    storeAdmissionDate &&
    !hasValidErpDateString(merged.strAtivDataAdmissao)
  ) {
    merged.strAtivDataAdmissao = storeAdmissionDate;
  }

  if (storeAdmissionDate && !hasValidErpDateString(merged.datTrabDesde)) {
    merged.datTrabDesde = storeAdmissionDate;
  }

  if (
    storeAdmissionDate &&
    !hasValidErpDateString(merged.datAtivDataAdmissao)
  ) {
    merged.datAtivDataAdmissao = storeAdmissionDate;
  }

  if (
    isPositiveNumber(storeSnapshot.dblRenda) &&
    !isPositiveNumber(merged.dblRenda)
  ) {
    merged.dblRenda = storeSnapshot.dblRenda;
  }

  if (
    isPositiveNumber(storeSnapshot.strRenda) &&
    !isPositiveNumber(merged.strRenda)
  ) {
    merged.strRenda = storeSnapshot.strRenda;
  }

  if (
    isPositiveNumber(storeSnapshot.dblComSalario) &&
    !isPositiveNumber(merged.dblComSalario)
  ) {
    merged.dblComSalario = storeSnapshot.dblComSalario;
  }

  if (
    isPositiveNumber(storeSnapshot.intDiaCartao) &&
    !isPositiveNumber(merged.intDiaCartao)
  ) {
    merged.intDiaCartao = storeSnapshot.intDiaCartao;
  }

  return merged;
};

/**
 * True when Unico process uses reused identity document for the CPF (iddocs + reused_doc).
 */
export const isUnicoReusedIdentityDocument = (
  processData: UnicoCreateProcessResponse | null | undefined
): boolean => {
  if (processData?.process?.flow !== Flow.iddocs) {
    return false;
  }
  const services = processData.process.services;
  if (!Array.isArray(services)) {
    return false;
  }
  for (const svc of services as UnicoProcessService[]) {
    const docs = svc?.documents;
    if (!Array.isArray(docs)) {
      continue;
    }
    for (const doc of docs) {
      if ((doc as UnicoProcessDocument)?.reused_doc === true) {
        return true;
      }
    }
  }
  return false;
};
